import React, { useState } from "react";
import { State, City } from "country-state-city";
import { SiteLevel } from "../../types/SiteLevel";
import { RegOrderErrs } from "../../stateManagement/reducers/ordersReducer";
import { FormHelperText } from "@mui/material";

interface LocationPickerProps {
  level?: SiteLevel;
  province: string;
  city: string;
  address: string;
  setProvince: (province: string) => void;
  setCity: (city: string) => void;
  setAddress: (address: string) => void;
  onChange?: (field: string) => void;
  errors?: RegOrderErrs;
}

export const LocationPicker: React.FC<LocationPickerProps> = ({
  level,
  province,
  city,
  address,
  setProvince,
  setCity,
  setAddress,
  onChange,
  errors,
}) => {
  const countryCode = "CA"; // Restrict to Canada
  const provinces = State.getStatesOfCountry(countryCode);

  // Fetch cities dynamically when a province is picked
  const cities = province ? City.getCitiesOfState(countryCode, province) : [];

  return (
    <div className="flex flex-col gap-4 max-w-full">
      {/* Province / Territory Select */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Province / Territory
        </label>
        <select
          value={province}
          onChange={(e) => {
            setProvince(e.target.value);
            setCity(""); // Reset city when province changes
            if (onChange) {
              onChange("state");
              onChange("city");
            }
          }}
          className="w-full border p-2 rounded-md"
        >
          <option value="">Select Province</option>
          {provinces.map((prov) => (
            <option key={prov.isoCode} value={prov.isoCode}>
              {prov.name} ({prov.isoCode})
            </option>
          ))}
        </select>
        {errors?.state && (
          <FormHelperText error id="helper-text-state">
            {errors.state}
          </FormHelperText>
        )}
      </div>

      {/* City Select */}
      {(!level || level === SiteLevel.ADDRESS || level === SiteLevel.CITY) && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City
          </label>
          <select
            value={city}
            onChange={(e) => {
              setCity(e.target.value);
              if (onChange) {
                onChange("city");
              }
            }}
            disabled={!province}
            className="w-full border p-2 rounded-md disabled:bg-gray-100"
          >
            <option value="">Select City</option>
            {cities.map((city) => (
              <option key={city.name} value={city.name}>
                {city.name}
              </option>
            ))}
          </select>
          {errors?.city && (
            <FormHelperText error id="helper-text-city">
              {errors.city}
            </FormHelperText>
          )}
        </div>
      )}
      {/* Street Address Input */}
      {(!level || level === SiteLevel.ADDRESS) && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Street Address
          </label>
          <input
            type="text"
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
              if (onChange) {
                onChange("address");
              }
            }}
            placeholder="e.g. 123 Yonge Street, Apt 4B"
            className="w-full border p-2 rounded-md"
          />
          {errors?.address && (
            <FormHelperText error id="helper-text-address">
              {errors.address}
            </FormHelperText>
          )}
        </div>
      )}
    </div>
  );
};
